import { GridColDef } from "@mui/x-data-grid-premium";
import { ReviewerStatsEntry } from "src/store/api/generatedApi";
import { CsvColumnDef } from "src/utils/csv";
import { userDisplayName } from "src/utils/userDisplay";

type Translate = (key: string) => string;
type FormatNumber = (value: number) => string;

export type ReviewerBaseRow = ReviewerStatsEntry & { id: string };

export const REVIEWER_NAME_FIELD = "name";

const reviewHoursPerHour = (row: ReviewerBaseRow): number | undefined =>
  row.total_time_spent_reviewing_seconds != null &&
  row.total_time_spent_reviewing_seconds > 0
    ? row.total_time_reviewed_seconds / row.total_time_spent_reviewing_seconds
    : undefined;

export const buildReviewerBaseColumns = (
  t: Translate,
  formatNumber1dp: FormatNumber
): GridColDef<ReviewerBaseRow>[] => [
  {
    field: REVIEWER_NAME_FIELD,
    headerName: t("name"),
    width: 160,
    valueGetter: (_value, row) => userDisplayName(row.reviewer),
  },
  {
    field: "email",
    headerName: t("email"),
    width: 200,
    valueGetter: (_value, row) => row.reviewer.email,
  },
  {
    field: "total_time_reviewed_seconds",
    headerName: t("totalTimeReviewed"),
    type: "number",
    width: 150,
    valueGetter: (_value, row) => row.total_time_reviewed_seconds / 3600,
    valueFormatter: (value: number) => `${formatNumber1dp(value)}h`,
  },
  {
    field: "total_time_spent_reviewing_seconds",
    headerName: t("totalTimeSpentReviewing"),
    type: "number",
    width: 180,
    valueGetter: (_value, row) =>
      row.total_time_spent_reviewing_seconds != null
        ? row.total_time_spent_reviewing_seconds / 3600
        : undefined,
    valueFormatter: (value?: number) =>
      value != null ? `${formatNumber1dp(value)}h` : "—",
  },
  {
    field: "review_hours_per_hour",
    headerName: t("reviewHoursPerHour"),
    type: "number",
    width: 150,
    valueGetter: (_value, row) => reviewHoursPerHour(row),
    valueFormatter: (value?: number) =>
      value != null ? formatNumber1dp(value) : "—",
  },
  {
    field: "total_submissions",
    headerName: t("totalSubmissions"),
    type: "number",
    width: 130,
  },
  {
    field: "periods_reviewed",
    headerName: t("periodsReviewed"),
    type: "number",
    width: 140,
  },
];

export const buildReviewerBaseCsvColumns = (
  t: Translate
): CsvColumnDef<ReviewerBaseRow>[] => [
  { header: t("name"), value: (row) => userDisplayName(row.reviewer) },
  { header: t("email"), value: (row) => row.reviewer.email },
  {
    header: t("totalTimeReviewed"),
    value: (row) => row.total_time_reviewed_seconds / 3600,
  },
  {
    header: t("totalTimeSpentReviewing"),
    value: (row) =>
      row.total_time_spent_reviewing_seconds != null
        ? row.total_time_spent_reviewing_seconds / 3600
        : null,
  },
  {
    header: t("reviewHoursPerHour"),
    value: (row) => reviewHoursPerHour(row) ?? null,
  },
  { header: t("totalSubmissions"), value: (row) => row.total_submissions },
  { header: t("periodsReviewed"), value: (row) => row.periods_reviewed },
];
